import { LayoutGrid, Menu } from "lucide-react";
import { useEffect, useState, type JSX, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { AccountMenu } from "../../profile/AccountMenu.js";
import { useUpdateSettings } from "../../profile/profileQueries.js";
import type { AppDescriptorDTO, MeDTO } from "../api/types.js";
import { orderApps } from "../apps/appOrder.js";
import { AppGrid } from "./AppGrid.js";
import { AppDrawer } from "./AppDrawer.js";
import { AppSidebar } from "./AppSidebar.js";
import type { MobileStart } from "./panelChoice.js";
import { TenantSwitcher } from "./TenantSwitcher.js";
import { GlobalSearch } from "../search/GlobalSearch.js";

/**
 * Gemeinsame Hülle aller buma-Apps (Epic #3), dreispaltig:
 *
 *   AppSidebar │ ModuleSidebar │ Inhalt
 *
 * Auf dem Telefon öffnet das Symbol oben links eine gemeinsame Schublade: links die
 * App-Symbole, daneben die Sidebar der aktiven App. Der Inhalt bleibt im Hauptpanel.
 */
export function AppShell({
  me,
  apps,
  moduleSidebar,
  baseRoute: _baseRoute,
  title,
  onSelectTenant,
  switchingTenant,
  onSignOut,
  mobileStart: _mobileStart = "sidebar",
  children,
}: {
  me: MeDTO;
  apps: AppDescriptorDTO[];
  moduleSidebar: ReactNode;
  /** Route der geöffneten App ohne Detail — Grundlage für „zurück zur Liste". */
  baseRoute: string;
  title: string;
  onSelectTenant: (slug: string) => void;
  switchingTenant: boolean;
  onSignOut: () => void;
  /** Womit das Telefon aufmacht, solange nichts ausgewählt ist. */
  mobileStart?: MobileStart;
  children: ReactNode;
}): JSX.Element {
  const location = useLocation();
  const [appsOpen, setAppsOpen] = useState(false);
  const [gridOpen, setGridOpen] = useState(false);
  const updateSettings = useUpdateSettings();

  // Die persönliche Reihenfolge gilt überall gleich: Leiste, Schublade, untere Leiste.
  const ordered = orderApps(apps, me.settings.appOrder);
  const reorder = (appOrder: string[]): void => updateSettings.mutate({ appOrder });

  useEffect(() => {
    setAppsOpen(false);
    setGridOpen(false);
  }, [location.pathname, location.search]);

  return (
    <div className="flex h-full flex-col bg-surface-sunken">
      <header className="flex min-h-16 shrink-0 items-center gap-1 border-b border-line bg-surface-raised px-2 py-2 pl-safe-left pr-safe-right pt-[calc(theme(spacing.2)+env(safe-area-inset-top))] sm:px-4">
        {/* Oben links: am Telefon die Leiste, am großen Bildschirm gleich das Raster —
            dort ist die Leiste ja ohnehin schon zu sehen. */}
        <button
          type="button"
          onClick={() => setAppsOpen(true)}
          aria-label="Bereiche öffnen"
          aria-expanded={appsOpen}
          className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-ink lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => setGridOpen(true)}
          aria-label="Alle Apps anzeigen"
          aria-expanded={gridOpen}
          className="hidden rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-ink lg:block"
        >
          <LayoutGrid className="h-5 w-5" />
        </button>

        <h1 className="ml-1 truncate text-sm font-semibold text-ink">{title}</h1>

        <div className="ml-auto flex items-center gap-2">
          <GlobalSearch />
          {/* Auf dem Telefon ist oben kein Platz für den Namen des Unternehmens —
              dort steht er im Konto-Menü unter dem Avatar. */}
          <div className="hidden sm:flex">
            <TenantSwitcher
              memberships={me.memberships}
              activeTenant={me.activeTenant}
              onSelect={onSelectTenant}
              busy={switchingTenant}
            />
          </div>
          {/* Avatar mit Status-Punkt; Mandant und Abmelden liegen im Menü darunter. */}
          <AccountMenu
            me={me}
            onSignOut={onSignOut}
            onSelectTenant={onSelectTenant}
            switchingTenant={switchingTenant}
          />
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <AppSidebar apps={ordered} variant="rail" onReorder={reorder} />

        {moduleSidebar ? (
          <aside
            className={[
              "shrink-0 overflow-y-auto border-line bg-surface-raised p-3",
              "hidden lg:block lg:w-72 lg:border-r",
            ].join(" ")}
          >
            {moduleSidebar}
          </aside>
        ) : null}

        {/* Das Panel selbst, ohne Polsterung: eine App, die die ganze Fläche braucht —
            der Chat — sitzt bündig darin. Wer Ränder will, bringt sie selbst mit. */}
        <main
          className={[
            "flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto bg-surface-raised",
          ].join(" ")}
        >
          {children}
        </main>
      </div>

      {/* Auf dem Telefon immer erreichbar; die Schublade oben links führt zu allen. */}
      <AppSidebar apps={ordered} variant="bottom" />

      {appsOpen ? (
        <AppDrawer apps={ordered} moduleSidebar={moduleSidebar} onClose={() => setAppsOpen(false)} onReorder={reorder}
          onGrid={() => { setAppsOpen(false); setGridOpen(true); }} />
      ) : null}

      {gridOpen ? <AppGrid apps={ordered} onClose={() => setGridOpen(false)} /> : null}
    </div>
  );
}
